import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) {
    const dest = user.role === "cashier" ? "/pos" : "/";
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen w-full flex bg-white">
      {/* Left: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-black text-xl text-slate-950 tracking-tight" style={{ fontFamily: "Manrope" }}>
                BANCA POS
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Sistema de bancas
              </div>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-950" style={{ fontFamily: "Manrope" }}>
            Iniciar sesión
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Acceso al sistema de bancas. República Dominicana.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                Usuario
              </Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario"
                className="mt-2 rounded-sm border-slate-300 h-11 text-base"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                Contraseña
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm border-slate-300 h-11 text-base pr-10"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                  data-testid="toggle-password"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2" role="alert" data-testid="login-error">
                {error}
                {/dispositivo autorizado/i.test(error) && (
                  <div className="mt-1 text-xs text-red-600 font-mono">
                    Si cambió de equipo, solicite al administrador revocar su dispositivo.
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-sm"
              data-testid="login-submit"
            >
              {loading ? "Ingresando…" : "Ingresar"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right: graphic panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-slate-950">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/9458996/pexels-photo-9458996.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/70 to-blue-950/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300">
              Sistema de Bancas · Rep. Dominicana
            </div>
            <div className="mt-6 text-4xl font-black leading-tight tracking-tight" style={{ fontFamily: "Manrope" }}>
              Vende más rápido.<br />Controla cada número.<br />Paga premios al instante.
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-md">
            {[
              { v: "10", l: "Loterías" },
              { v: "17+", l: "Juegos" },
              { v: "6", l: "Tipos de jugada" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-blue-500 pl-3">
                <div className="text-3xl font-black font-mono">{s.v}</div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-300">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
